import React, { useState } from "react";
import localConstants from "../views/shared/constants";
import planningService from "features/planning/planningService";
import { fillCreatePlanningItems } from "../views/shared/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Trans, useTranslations } from "adapters/i18n";

dayjs.extend(utc);

export const useSubmitUtils = ({
  farmId,
  enableLoading,
  disableLoading,
  notifyError,
  notifySuccess,
  callbackSubmit,
}) => {
  const { t } = useTranslations();
  const [showConfirmationModal, setConfirmationModal] = useState(null);

  const onCloseConfirmModal = () => {
    setConfirmationModal(null);
  };

  const showModalError = (title, text) => {
    setConfirmationModal({
      title,
      text,
      buttonConfirmText: t("common:actions.continue"),
      onConfirm: onCloseConfirmModal,
      onCancel: null,
    });
  };

  const saveMessage = (formattedInitialDate, formattedFinalDate) => (
    <span>
      <Trans i18nKey="planning:register.save-existing-error"
        values={{ formattedInitialDate, formattedFinalDate }}
        components={{ strong: <strong /> }}
      />
    </span>
  );

  const editMessage = (formattedInitialDate, formattedFinalDate) => (
    <span>
      <Trans i18nKey="planning:edit.edit-existing-error"
        values={{ formattedInitialDate, formattedFinalDate }}
        components={{ strong: <strong /> }}
      />
    </span>
  );

  const showErrorModalDateExist = (planning) => {
    const formattedInitialDate = dayjs
      .utc(planning.initialDate)
      .format(localConstants.dateShortFormat);
    const formattedFinalDate = dayjs
      .utc(planning.finalDate)
      .format(localConstants.dateShortFormat);
    const title = planning.id ? t("planning:edit.edit-planning") : t("planning:register.save-planning");
    const text = planning.id
      ? editMessage(formattedInitialDate, formattedFinalDate)
      : saveMessage(formattedInitialDate, formattedFinalDate);
    showModalError(title, text);
  };

  const checkErrorPlanning = (error, planning) => {
    if (
      error.response &&
      error.response.status === 400 &&
      error.response.data &&
      error.response.data.message
    ) {
      let periodAlreadyExists = error.response.data.message.includes(
        "period has already been saved"
      );
      if (periodAlreadyExists) {
        showErrorModalDateExist(planning);
      } else {
        notifyError(error.response.data.message);
      }
    }
  };

  const checkNotifyError = (error) => {
    if (error.response && error.response.status === 500) {
      notifyError(t("errors:planning.save"));
    }
  };

  function createPlanData(
    planningList,
    summaryReport,
    formulas,
    ingredientsPrices,
    farmId,
    initialDate,
    finalDate
  ) {
    const planningItems = fillCreatePlanningItems(planningList);
    const planningCategories = summaryReport.categoryPlanning.categories.map(
      (item) => item.category
    );
    const planningFormulasIds = summaryReport.formulaPlanning.map(
      (item) => item.formula.id
    );
    const planningFormulas = formulas
      .filter((formula) => planningFormulasIds.includes(formula.IdFormula))
      .map((formula) => ({
        id: formula.IdFormula,
        name: formula.Nome,
        ingredients: formula.Ingrediente.map((i) => ({
          id: i.OperacaoIngrediente.IdIngrediente,
          name: i.OperacaoIngrediente.Descricao,
        })),
      }));
    const ingredientCosts = summaryReport.ingredientPlanning
      .filter((item) => ingredientsPrices[item.ingredient.id])
      .map((item) => ({
        ingredientId: item.ingredient.id,
        ingredientName: item.ingredient.name,
        cost: ingredientsPrices[item.ingredient.id],
      }));

    const planningDataToRegister = {
      farmId: farmId,
      initialDate: dayjs
        .utc(initialDate)
        .startOf("day")
        .startOf("month")
        .toISOString(),
      finalDate: dayjs
        .utc(finalDate)
        .startOf("day")
        .startOf("month")
        .toISOString(),
      planningItems,
      formulas: planningFormulas,
      categories: planningCategories,
      ingredientCosts,
      total: summaryReport.planning.total,
      reports: {
        formulaPlanning: summaryReport.formulaPlanning,
        ingredientPlanning: summaryReport.ingredientPlanning,
        categoryPlanning: summaryReport.categoryPlanning,
      },
    };
    return planningDataToRegister;
  }

  const onCreatePlan = async ({
    initialDate,
    finalDate,
    planningList,
    ingredientsPrices,
    summaryReport,
    formulas,
  }) => {
    const planningDataToRegister = createPlanData(
      planningList,
      summaryReport,
      formulas,
      ingredientsPrices,
      farmId,
      initialDate,
      finalDate
    );

    try {
      enableLoading();
      await planningService.postPlan(planningDataToRegister);
      callbackSubmit && callbackSubmit();
      notifySuccess(t("planning:register.success-save"));
    } catch (error) {
      checkErrorPlanning(error, planningDataToRegister);
      checkNotifyError(error);
    } finally {
      disableLoading();
    }
  };

  const onEditPlan = async (
    planningCode,
    {
      initialDate,
      finalDate,
      planningList,
      ingredientsPrices,
      summaryReport,
      formulas,
    }
  ) => {
    const planningDataToRegister = createPlanData(
      planningList,
      summaryReport,
      formulas,
      ingredientsPrices,
      farmId,
      initialDate,
      finalDate
    );

    try {
      enableLoading();
      await planningService.postNewVersion(
        planningCode,
        planningDataToRegister
      );
      callbackSubmit && callbackSubmit();
      notifySuccess(t("planning:edit.success-edit"));
    } catch (error) {
      checkErrorPlanning(error, planningDataToRegister);
      checkNotifyError(error);
    } finally {
      disableLoading();
    }
  };

  return [
    showConfirmationModal,
    {
      onCreatePlan,
      onEditPlan,
      onCloseConfirmModal,
    },
  ];
};
